//@ts-nocheck
import React, { useState, useEffect, useRef } from "react";
import { Paperclip, X } from "lucide-react";
import { toast } from "sonner";
import {
  createTeamMessageThunk,
  deleteTeamMemberMessageData,
  fetchTeamMessagesListThunk,
  updateTeamMessageAsync,
} from "../../store/slices/message/messageSlice";
import { useParams } from "react-router-dom";
import { formatDistanceToNow } from "date-fns";
import {
  Dialog,
  DialogBackdrop,
  DialogPanel,
  DialogTitle,
} from "@headlessui/react";
import { CrossIcon } from "../../components/icons/Icons";
import { getColorForLabel } from "../../utils/colorUtils";
import CustomMenuDropdown from "../../components/CustomMenuDropdown";
import { useAppDispatch } from "../../hooks/useAppDispatch";

// Auto-resizing textarea component
const AutoResizeTextarea = ({ value, onChange, placeholder }) => {
  const textareaRef = useRef(null);

  useEffect(() => {
    if (textareaRef.current) {
      // Reset height to auto to get the correct scrollHeight
      textareaRef.current.style.height = "auto";
      // Set the height based on scrollHeight with a minimum of 60px
      textareaRef.current.style.height = `${Math.max(60, textareaRef.current.scrollHeight)}px`;
    }
  }, [value]);

  return (
    <textarea
      ref={textareaRef}
      className="outline-none bg-transparent focus:outline-none focus:ring-0 text-[14px] text-dark-black-100 w-full m-0 placeholder:text-[#9e9ea0] border-1 border-solid border-light-gray-200 rounded-md p-2 min-h-[60px] overflow-hidden resize-none"
      placeholder={placeholder}
      value={value}
      onChange={onChange}
      rows={1}
    />
  );
};

const EditMessageModal = ({
  isOpen,
  onClose,
  message,
  onSave,
  loadingMessageUpdate,
}) => {
  const [updatedMessage, setUpdatedMessage] = useState("");

  useEffect(() => {
    if (message?.message) {
      setUpdatedMessage(message.message);
    }
  }, [message]); // Re-run this effect when `message` changes

  if (!isOpen) return null;

  return (
    <Dialog open={isOpen} onClose={onClose} className="relative z-10 shruti">
      <DialogBackdrop
        transition
        className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in"
      />
      <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
        <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
          <DialogPanel
            transition
            className="relative transform rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all data-[closed]:translate-y-4 data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in sm:my-8 sm:w-full sm:max-w-lg sm:p-6 data-[closed]:sm:translate-y-0 data-[closed]:sm:scale-95"
          >
            <h3 className="text-base font-semibold leading-6 text-gray-900">
              Edit Message
            </h3>
            <div className="absolute right-0 top-0 hidden pr-4 pt-4 sm:block">
              <button
                type="button"
                onClick={onClose}
                className="rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
              >
                <span className="sr-only">Close</span>
                <CrossIcon />
              </button>
            </div>

            <div className="mt-5">
              <textarea
                className="outline-none bg-transparent focus:outline-none focus:ring-0 text-[14px] text-dark-black-100 w-full m-0 placeholder:text-[#9e9ea0] border-1 border-solid border-light-gray-200 rounded-md p-2"
                value={updatedMessage}
                onChange={(e) => setUpdatedMessage(e.target.value)}
              />
            </div>

            <div className="mt-5 sm:mt-4 flex gap-2 justify-end">
              <button
                type="button"
                onClick={onClose}
                className="items-center px-[14px] py-1 text-[13px] font-medium h-[32px] inline-flex leading-6 w-fit justify-center rounded-md bg-white text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto"
              >
                Cancel
              </button>
              <button
                onClick={() => onSave(updatedMessage)}
                type="button"
                className="rounded-md bg-indigo-600 items-center px-[14px] py-1 text-[13px] font-medium h-[32px] text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 "
              >
                {loadingMessageUpdate ? (
                  <div className="flex items-center gap-[6px]">
                    Save
                    <div className="simple-spinner">
                      <span></span>
                    </div>
                  </div>
                ) : (
                  " Save"
                )}
              </button>
            </div>
          </DialogPanel>
        </div>
      </div>
    </Dialog>
  );
};

function ApplicationMessages({ job: jobProp, app: appProp, quickData }) {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [message, setMessage] = useState("");
  const [showButton, setShowButton] = useState("");
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const { base_slug, jobId, appId } = useParams();
  const dispatch = useAppDispatch();
  const [page, setPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);
  const [loadingMessage, setLoadingMessage] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [loadingMessageUpdate, setLoadingMessageUpdate] = useState(false);
  const [openDeleteModalExp, setOpenDeleteModalExp] = useState(false);
  const [deleteLoadingExp, setDeleteLoadingExp] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(null);
  const [saveName, setSaveName] = useState("");

  const handleEditClick = (message) => {
    setSelectedMessage(message);
    setIsModalOpen(true);
  };

  const handleSave = async (updatedMessage) => {
    if (selectedMessage) {
      setLoadingMessageUpdate(true);
      try {
        await dispatch(
          updateTeamMessageAsync({
            id: selectedMessage.id,
            messageContent: updatedMessage,
          }),
        ).unwrap();
        toast.success("Message updated successfully!");
        setIsModalOpen(false);
        // Optionally, refresh the messages list
        const res = await dispatch(
          fetchTeamMessagesListThunk({
            appID: appId,
            // type: 'message',
            page: page,
            limit: itemsPerPage,
          }),
        ).unwrap();
        if (res) {
          setMessages(res.data);
          setShowButton(res);
        }
      } catch (error) {
        toast.error("Failed to update message");
      } finally {
        setLoadingMessageUpdate(false);
      }
    }
  };

  useEffect(() => {
    const fetchMessages = async () => {
      if (jobId && appId) {
        setLoading(true);
        try {
          const res = await dispatch(
            fetchTeamMessagesListThunk({
              appID: appId,
              // type: 'message',
              page: page,
              limit: itemsPerPage,
            }),
          ).unwrap();
          if (res) {
            setMessages((prevMessages) => [...prevMessages, ...res?.data]);
            setShowButton(res);
          }
        } catch (error) {
          console.error("Error fetching messages:", error);
        } finally {
          setLoading(false);
        }
      }
    };

    fetchMessages();
  }, [jobId, appId, dispatch, page, itemsPerPage]);

  const handleLoadMore = () => {
    setItemsPerPage((prevItemsPerPage) => prevItemsPerPage);
    setPage((prevPage) => prevPage + 1);
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files.length > 0) {
      const file = event.target.files[0];

      // Validate file type
      const allowedTypes = [
        "application/pdf",
        "application/msword",
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      ];
      if (!allowedTypes.includes(file.type)) {
        toast.error("Only PDF or DOC files are allowed.");
        return;
      }

      // Validate file size (20 MB limit)
      const maxSize = 20 * 1024 * 1024; // 20 MB in bytes
      if (file.size > maxSize) {
        toast.error("File size must be less than 20 MB.");
        return;
      }

      // If validation passes, set the selected file
      setSelectedFile(file);
    }
  };

  const removeFile = () => {
    setSelectedFile(null);
  };

  const handlePostMessage = async () => {
    if (!message.trim()) {
      toast.error("Message cannot be empty");
      return;
    }
    setLoadingMessage(true);

    const formData = new FormData();
    formData.append("job_id", jobId || "");
    formData.append("app_id", appId || "");
    formData.append("type", "message");
    formData.append("privacy", "public");
    formData.append("message", message);

    if (selectedFile) {
      formData.append("attachment", selectedFile);
    }

    try {
      await dispatch(createTeamMessageThunk({ formData })).unwrap();
      toast.success("Message posted successfully!");
      setMessage("");
      setSelectedFile(null); // Clear the selected file after posting

      const res = await dispatch(
        fetchTeamMessagesListThunk({
          appID: appId,
          // type: 'message',
          page: 1,
          limit: itemsPerPage,
        }),
      ).unwrap();
      if (res.data) {
        setMessages(res.data);
      }
    } catch (error) {
      console.error("Failed to post message:", error);
      toast.error("Failed to post message");
    } finally {
      setLoadingMessage(false);
    }
  };

  const userDetailsData = JSON.parse(
    localStorage.getItem("baseMembersDetails") || "{}",
  );
  const baseMembers = userDetailsData?.data?.baseMembers || [];

  const handleCloseModal = () => {
    setOpenDeleteModalExp(false);
    setSelectedIndex(null); // Clear the selected index
  };

  const handleDeleteClick = (message) => {
    setSelectedIndex(message.id);
    setOpenDeleteModalExp(true);
  };

  const handleDeleteConfirm = async () => {
    setDeleteLoadingExp(true); // Start loading

    try {
      await dispatch(
        deleteTeamMemberMessageData({
          id: selectedIndex,
        }),
      ).unwrap();

      toast.success("Message deleted successfully!");
      setOpenDeleteModalExp(false);

      const res = await dispatch(
        fetchTeamMessagesListThunk({
          appID: appId,
          // type: "message",
          page: page,
          limit: itemsPerPage,
        }),
      ).unwrap();

      if (res) {
        setMessages(res.data);
        setShowButton(res);
      }
    } catch (error) {
      toast.error("Failed to delete message!");
      console.error("Delete error:", error);
    } finally {
      setDeleteLoadingExp(false); // Stop loading
    }
  };

  useEffect(() => {
    const name = localStorage.getItem("name");
    if (name) {
      setSaveName(name);
    }
  }, []);

  return (
    <div className="mt-[0px] px-4 lg:px-2 py-4 lg:py-[32px] h-[calc(91vh_-_115px)] lg:h-[calc(100vh_-_115px)] rounded-b-lg bg-white overflow-y-auto">
      <div className="w-full max-w-[710px] mx-auto">
        <h2 className="text-[16px] lg:text-lg font-medium text-gray-900">
          Message
        </h2>
        <div className="bg-[#e0e0e0] w-full h-[1px] mt-4 mb-[22px] lg:mb-[34px] lg:mt-7"></div>
        <div className="space-y-4">
          {showButton.hasNextPage === true && (
            <div className="flex justify-center mt-0">
              <button
                className="text-blue-500 leading-5 text-[12px] rounded-[60px] py-1 hover:ring-1 px-3 ring-inset ring-gray-500/10"
                type="button"
                onClick={handleLoadMore}
              >
                <span className="px-1">Load More</span>
              </button>
            </div>
          )}
          {loading ? (
            <div
              className="flex items-center justify-center relative z-50"
              style={{ height: "100px" }}
            >
              <div className="simple-spinner-loader">
                <span></span>
              </div>
            </div>
          ) : messages.length === 0 ? (
            <div className="w-full flex h-[100px] justify-center items-center flex-col gap-y-3">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width={24}
                height={24}
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth={2}
                strokeLinecap="round"
                strokeLinejoin="round"
                className="icon icon-tabler icons-tabler-outline icon-tabler-info-triangle  w-[1.5rem] h-[1.5rem] text-[#808080]"
              >
                <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                <path d="M10.363 3.591l-8.106 13.534a1.914 1.914 0 0 0 1.636 2.871h16.214a1.914 1.914 0 0 0 1.636 -2.87l-8.106 -13.536a1.914 1.914 0 0 0 -3.274 0z" />
                <path d="M12 9h.01" />
                <path d="M11 12h1v4h1" />
              </svg>
              <p className="text-[#0000007a] text-[13px]">No results found</p>
            </div>
          ) : (
            [...messages].reverse().map((msg, index) => {
              const dropdownGroups = [
                {
                  options: [
                    {
                      label: `Edit`,
                      className:
                        "py-[0.375rem] px-[0.75rem] bg-white text-[#000000eb] text-[13px] hover:bg-[#e7e7e8]",
                      onClick: () => handleEditClick(msg),
                    },
                    {
                      label: `Delete`,
                      className:
                        "py-[0.375rem] px-[0.75rem] bg-white text-[#000000eb] text-[13px] hover:bg-[#e7e7e8]",
                      onClick: () => handleDeleteClick(msg),
                    },
                  ],
                },
              ];

              const renderMessageWithMentions = (
                messageText,
                pipelineStageDataUser = [],
              ) => {
                if (!messageText) return "";

                // Safety check for pipelineStageDataUser
                if (!Array.isArray(pipelineStageDataUser)) {
                  return messageText;
                }

                // Create a map of user IDs to names for quick lookup
                const userIdToNameMap = {};
                pipelineStageDataUser.forEach((user) => {
                  if (user && user.id) {
                    userIdToNameMap[user.id] = user.name || `User ${user.id}`;
                  }
                });

                // Split the message by the mention pattern {{userId}}
                // The capturing group (...) ensures the matched markers are kept in the resulting array
                const parts = messageText.split(/(\{\{\d+\}\})/g);

                return parts.map((part, index) => {
                  // Check if the current part is a mention marker like {{10015}}
                  const match = part.match(/^\{\{(\d+)\}\}$/);

                  if (match) {
                    const userId = match[1];
                    const userName =
                      userIdToNameMap[userId] || `User ${userId}`;

                    // Return the styled mention element
                    return (
                      <span key={index} className="text-blue-800 px-1 rounded">
                        @{userName}
                      </span>
                    );
                  }

                  // Return regular text parts as they are
                  return <span key={index}>{part}</span>;
                });
              };

              return (
                <div
                  key={index}
                  className="rounded-md p-3 cursor-pointer ring-1 ring-gray-200 feedback-delete-hover"
                >
                  <div className="flex items-start">
                    <div className="w-[95%] flex flex-col gap-1">
                      <div className="flex justify-start items-center gap-x-2">
                        <span className="flex items-center gap-x-3">
                          {msg.profile_pic || msg?.name ? (
                            <img
                              src={`${process.env.REACT_APP_API_BASE_URL}/${msg?.profile_pic}`}
                              alt={`${msg.name || "Profile"}`}
                              className="h-6 w-6 flex-none rounded-full ring-1 ring-gray-900/10"
                            />
                          ) : (
                            <div
                              style={{
                                backgroundColor: getColorForLabel(
                                  msg?.name?.charAt(0),
                                ),
                              }}
                              className="w-6 h-6 rounded-full flex items-center justify-center leading-5"
                            >
                              <span className="font-medium text-white leading-6">
                                {msg?.name?.charAt(0) || saveName.charAt(0)}
                              </span>
                            </div>
                          )}
                        </span>
                        <div className="flex gap-x-[6px] items-center">
                          {msg.name && (
                            <span className="text-[12px] text-[#374151] font-medium leading-5 capitalize">
                              {msg.name}
                            </span>
                          )}
                          {msg.user_type === "candidate" && (
                            <span className="text-[12px] text-[#374151] font-medium leading-5 capitalize">
                              {saveName}
                            </span>
                          )}

                          <span className="flex items-center gap-x-[6px] text-xs leading-5 text-gray-500">
                            <span className="flex h-[4px] w-[4px] shrink-0 items-center justify-center rounded-full border-none text-[0.625rem] font-medium text-white capitalize bg-[#6b7280]"></span>
                            {formatDistanceToNow(new Date(msg.created_at), {
                              addSuffix: true,
                              includeSeconds: true,
                            })
                              .replace("about ", "")
                              .replace("less than ", "")
                              .replace("half a ", "1 ")}
                          </span>
                          {msg.user_type === "team" && (
                            <span className="flex items-center gap-[6px] text-[13px] rounded-md bg-gray-50 px-2 py-1  text-xs font-medium text-gray-600 ring-1 ring-inset ring-gray-500/10 capitalize w-fit">
                              Team
                            </span>
                          )}
                        </div>
                      </div>
                      <p className="font-normal text-[#374151] text-[14px] wg-antialiased leading-6 whitespace-break-spaces">
                        {/* {msg.message} */}
                        {renderMessageWithMentions(
                          msg.message,
                          msg?.mentions || [],
                        )}
                      </p>
                      {msg.attachment && (
                        <div className="mt-2 flex items-center gap-2 bg-gray-100 p-2 rounded-md">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width={24}
                            height={24}
                            viewBox="0 0 24 24"
                            fill="currentColor"
                            className="icon icon-tabler icons-tabler-filled icon-tabler-file-check  w-[1rem] h-[1rem] text-[#808080]"
                          >
                            <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                            <path d="M12 2l.117 .007a1 1 0 0 1 .876 .876l.007 .117v4l.005 .15a2 2 0 0 0 1.838 1.844l.157 .006h4l.117 .007a1 1 0 0 1 .876 .876l.007 .117v9a3 3 0 0 1 -2.824 2.995l-.176 .005h-10a3 3 0 0 1 -2.995 -2.824l-.005 -.176v-14a3 3 0 0 1 2.824 -2.995l.176 -.005zm3.707 10.293a1 1 0 0 0 -1.414 0l-3.293 3.292l-1.293 -1.292a1 1 0 1 0 -1.414 1.414l2 2a1 1 0 0 0 1.414 0l4 -4a1 1 0 0 0 0 -1.414m-.707 -9.294l4 4.001h-4z" />
                          </svg>

                          <a
                            href={`${process.env.REACT_APP_API_BASE_URL}/${msg.attachment}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="font-normal text-[#374151] text-[13px] wg-antialiased leading-6 whitespace-pre"
                          >
                            {msg.attachment}
                          </a>
                        </div>
                      )}
                    </div>

                    <div className="w-[5%]">
                      <div className="[&>div]:shrink feedback-delete-btn-hover">
                        <CustomMenuDropdown
                          sections={dropdownGroups}
                          icon={
                            <svg
                              stroke="currentColor"
                              fill="none"
                              strokeWidth="2"
                              viewBox="0 0 24 24"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              aria-hidden="true"
                              focusable="false"
                              height="1em"
                              width="1em"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <circle cx="12" cy="12" r="1"></circle>
                              <circle cx="12" cy="5" r="1"></circle>
                              <circle cx="12" cy="19" r="1"></circle>
                            </svg>
                          }
                          menuItemsClassName="left-auto"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>

        <div className="mt-8">
          <AutoResizeTextarea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="Write a message..."
          />
          <div className="flex items-center justify-between my-8">
            <div className="relative flex items-center gap-2">
              <label className="cursor-pointer flex items-center gap-2 text-gray-500">
                <Paperclip size={18} />
                <input
                  type="file"
                  className="hidden"
                  onChange={handleFileChange}
                />
              </label>
              {selectedFile && (
                <div className="flex items-center gap-2 bg-gray-100 p-1 px-2 rounded-md text-gray-700">
                  <span className="text-sm">{selectedFile.name}</span>
                  <X
                    size={16}
                    className="cursor-pointer text-gray-500 hover:text-red-500"
                    onClick={removeFile}
                  />
                </div>
              )}
            </div>
            <button
              className="group inline-flex shrink-0 select-none justify-center items-center px-[14px] py-1 text-[13px] font-medium h-[32px] leading-6 transition-colors duration-100 
            focus:outline-0 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 disabled:pointer-events-none gap-0  rounded-md text-white bg-indigo-600 hover:bg-indigo-500 focus-visible:outline-indigo-600"
              type="button"
              onClick={handlePostMessage}
            >
              <div className="flex items-center  gap-[6px]">
                Post
                {loadingMessage && (
                  <div className="simple-spinner">
                    <span></span>
                  </div>
                )}
              </div>
            </button>
          </div>
        </div>

        <EditMessageModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          message={selectedMessage}
          onSave={handleSave}
          loadingMessageUpdate={loadingMessageUpdate}
        />

        {openDeleteModalExp && (
          <Dialog
            open={true}
            onClose={handleCloseModal}
            className="relative z-10 shruti"
          >
            <DialogBackdrop
              transition
              className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in"
            />
            <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
              <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
                <DialogPanel
                  transition
                  className="relative transform rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all data-[closed]:translate-y-4 data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in sm:my-8 sm:w-full sm:max-w-lg sm:p-6 data-[closed]:sm:translate-y-0 data-[closed]:sm:scale-95"
                >
                  <div className="absolute right-0 top-0 hidden pr-4 pt-4 sm:block">
                    <button
                      type="button"
                      onClick={handleCloseModal}
                      className="rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                    >
                      <span className="sr-only">Close</span>
                      <CrossIcon />
                    </button>
                  </div>
                  <div className="flex flex-col gay-y-4">
                    <span className="leading-6 text-sm text-gray-500">
                      Do you want to sure delete this Message?
                    </span>
                  </div>
                  <div className="mt-5 sm:mt-4 flex gap-2 justify-end">
                    <button
                      type="button"
                      onClick={handleCloseModal}
                      className="items-center px-[14px] py-1 text-[13px] font-medium h-[32px] inline-flex leading-6 w-fit justify-center rounded-md bg-white text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleDeleteConfirm}
                      type="button"
                      className="rounded-md bg-indigo-600 items-center px-[14px] py-1 text-[13px] font-medium h-[32px] text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 "
                    >
                      {deleteLoadingExp ? (
                        <div className="flex items-center gap-[6px]">
                          Delete
                          <div className="simple-spinner">
                            <span></span>
                          </div>
                        </div>
                      ) : (
                        "Delete"
                      )}
                    </button>
                  </div>
                </DialogPanel>
              </div>
            </div>
          </Dialog>
        )}
      </div>
    </div>
  );
}

export default ApplicationMessages;
